import React, {useContext, useState, useEffect, useRef} from 'react';
import {StyleSheet, Text, Pressable, View} from 'react-native';
import DataOps from '../../data/DataOps';
import {GlobalStateContext} from '../general/GlobalStateProvider';
import { isParentAccount } from '../../interface/AccountType';
import {toast} from 'react-toastify';
import {RegistrationStatus} from '../../data/SessionInterface';
import {getRegistrationStatusConfig, RegistrationStatusConfig} from '../../data/RegistrationStatusConfig';
import TimeOps from '../../data/TimeOps';
import CoachViewModal from './CoachViewModal';
import ChildSelectionModal from './ChildSelectionModal';
import WaiverAcknowledgeModal from './WaiverAcknowledgeModal';
import NotificationOps from '../../data/NotificationOps';
import RegistrationOps from '../../data/RegistrationOps';

const RegistrationBanner = ({
                                sessionId,
                                updateValue=(key: string, value: string)=>{},
                                isEditing=false,
                                isHost=false,
                                registeredUser=false,
                                session=null,
                                initialView=null,
                                initialDate=null,
                            }) => {
    const {user: user, setUser, likedSessions, setLikedSessions, setShowLoginModal} = useContext(GlobalStateContext);

    const [isOpen, setIsOpen] = useState(false);
    const [showCoachModal, setShowCoachModal] = useState(false);
    const [showChildSelectionModal, setShowChildSelectionModal] = useState(false);
    const [showWaiverModal, setShowWaiverModal] = useState(false);
    const [registeredChildIds, setRegisteredChildIds] = useState<string[]>([]);

    const bannerRef = useRef(null);

    // Use TimeOps for session start checking
    const hasSessionStarted = () => TimeOps.hasSessionStarted(session);
    
    // Update registration status based on session state and user type
    const getEffectiveRegistrationStatus = () => {
        // Helper function to check if session is full
        const isSessionFull = () => {
            if (!session || !session.capacity) return false;
            const registeredCount = session.registeredChildren ? Object.keys(session.registeredChildren).length : 0;
            return registeredCount >= parseInt(session.capacity);
        };

        // Check if user can register (only family accounts can register)
        const canRegister = !user || isParentAccount(user.accountType);

        if (hasSessionStarted()) {
            // Session has already started
            if (isHost) {
                return 'COACH_VIEW';
            } else if (registeredUser && canRegister) {
                // Registered family accounts can view their session
                return 'CAMPER_VIEW';
            } else {
                // Unregistered users or non-family accounts cannot view
                return 'DISABLED_VIEW';
            }
        }
        
        // For sessions that haven't started yet
        if (isHost) {
            return 'COACH_VIEW';
        } else if (canRegister) {
            // All family accounts see REGISTER before session starts (can manage registrations)
            return isSessionFull() ? 'FULL' : 'REGISTER';
        } else {
            // Non-family accounts cannot register
            return 'DISABLED_VIEW';
        }
    };
    
    const effectiveRegistrationStatus = getEffectiveRegistrationStatus();
    const [selectedOption, setSelectedOption] = useState(effectiveRegistrationStatus);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (bannerRef.current && !bannerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const updateRegisteredChildren = async (childIds: string[]) => {
        // Create transaction records and registration records; do NOT mutate session.registeredChildren
        for (const childId of childIds) {
            const child = user.children.find(c => c.childId === childId);
            if (!child) continue;
            try {
                const hostId = session?.hostUserId || null;
                const coachId = session?.coach?.userId || null;
                // Session transaction record
                await DataOps.createSessionTransaction({
                    sessionId,
                    campId: session.campId,
                    coachId,
                    hostId,
                    userId: user.userId,
                    childId,
                    status: 'registered',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });
            } catch (e) {}
            try {
                const hostId = session?.hostUserId || null;
                const coachId = session?.coach?.userId || null;
                // Backward-compatible registration record
                await RegistrationOps.createConfirmed(session.campId, sessionId, user.userId, childId, coachId, hostId);
            } catch (e) {}
        }
    }

    const updateUnregisteredChildren = async (childIds: string[]) => {
        // Create transaction records and cancel registration records; do NOT mutate session.registeredChildren
        for (const childId of childIds) {
            try {
                const hostId = session?.hostUserId || null;
                const coachId = session?.coach?.userId || null;
                await DataOps.createSessionTransaction({
                    sessionId,
                    campId: session.campId,
                    coachId,
                    hostId,
                    userId: user.userId,
                    childId,
                    status: 'unregistered',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });
            } catch (e) {}
            try {
                await RegistrationOps.cancel(sessionId, user.userId, childId, user.userId);
            } catch (e) {}
        }
    }

    const updateChildrenUpcomingSessions = async (childIds: string[]) => {
        const userData = await DataOps.fetchDataFromFirebase('user', user.userId);
        
        // Update each child's registered sessions
        for (const childId of childIds) {
            const childIndex = userData.children.findIndex(c => c.childId === childId);
            if (childIndex !== -1) {
                userData.children[childIndex].registeredSessions = 
                    userData.children[childIndex].registeredSessions || [];
                
                if (!userData.children[childIndex].registeredSessions.includes(sessionId)) {
                    userData.children[childIndex].registeredSessions.push(sessionId);
                }
            }
        }

        await DataOps.saveCustomDataToFirebase('user', user.userId, userData, true);
    }

    const removeChildrenUpcomingSessions = async (childIds: string[]) => {
        const userData = await DataOps.fetchDataFromFirebase('user', user.userId);
        
        // Remove session from each child's registered sessions
        for (const childId of childIds) {
            const childIndex = userData.children.findIndex(c => c.childId === childId);
            if (childIndex !== -1) {
                userData.children[childIndex].registeredSessions = 
                    userData.children[childIndex].registeredSessions || [];
                
                userData.children[childIndex].registeredSessions = 
                    userData.children[childIndex].registeredSessions.filter(id => id !== sessionId);
            }
        }

        await DataOps.saveCustomDataToFirebase('user', user.userId, userData, true);
    }

    // Load parent's already-registered childIds for this session from registration table
    useEffect(() => {
        const loadRegisteredForParent = async () => {
            try {
                if (!showChildSelectionModal || !sessionId || !user?.userId) return;
                const regs = await DataOps.fetchActiveRegistrations(sessionId);
                const mine = (regs || []).filter((r: any) => r.userId === user.userId);
                const childIds = Array.from(new Set(mine.map((r: any) => r.childId).filter(Boolean)));
                setRegisteredChildIds(childIds);
            } catch {}
        };
        loadRegisteredForParent();
    }, [showChildSelectionModal, sessionId, user?.userId]);

    const onBannerPress = async () => {
        if (!isEditing) {
            // Check if user is logged in for registration actions
            if (!user && effectiveRegistrationStatus === 'REGISTER') {
                setShowLoginModal(true);
                return;
            }
            
            // Prevent non-family accounts from registering
            if (user && !isParentAccount(user.accountType) && effectiveRegistrationStatus === 'REGISTER') {
                toast.error('Only family accounts can register for sessions');
                return;
            }
            
            // Show child selection modal for family accounts when clicking register
            if (effectiveRegistrationStatus === 'REGISTER' && isParentAccount(user?.accountType)) {
                // Check if user has children in their profile
                if (!user.children || user.children.length === 0) {
                    toast.warning('Please add children to your family profile before registering for sessions');
                    return;
                }
                // If session requires a waiver, show acknowledgment first
                if (session?.waiverRequired && session?.waiverUrl) {
                    setShowWaiverModal(true);
                    return;
                }
                setShowChildSelectionModal(true);
                return;
            }
            
            const config = getRegistrationStatusConfig(effectiveRegistrationStatus);
            
            if (config.disabled) {
                return; // Do nothing for disabled status
            }
            
            // Open coach modal for coach view
            if (effectiveRegistrationStatus === 'COACH_VIEW') {
                setShowCoachModal(true);
                return;
            }
            
            // Open coach modal for camper view (with different props)
            if (effectiveRegistrationStatus === 'CAMPER_VIEW') {
                setShowCoachModal(true);
                return;
            }
            
            if (config.toastMessage) {
                const toastType = effectiveRegistrationStatus === 'FULL' ? 'error' : 
                                 effectiveRegistrationStatus === 'REGISTER' ? 'info' : 'success';
                toast[toastType](config.toastMessage);
            }
        } else {
            setIsOpen(!isOpen);
        }
    }
    
    const sendNotificationsToHostAndCoach = async (childNames: string[], action: 'register' | 'unregister') => {
        try {
            if (!session) return;
            
            const campId = session.campId;
            const sessionTitle = session.title || 'Untitled Session';
            const parentName = user?.userName || `${user?.firstName} ${user?.lastName}`;
            
            // Resolve host user more efficiently via camp document if possible
            let hostUserId: string | null = null;
            try {
                const campData: any = await DataOps.fetchDataFromFirebase('camp', campId);
                if (campData?.hostUserId) {
                    hostUserId = campData.hostUserId;
                }
            } catch {}
            
            if (!hostUserId) {
                // Fallback: scan users collection to find host who owns this camp
                const allUsers = await DataOps.fetchAllDocumentsFromFirebase('user');
                if (allUsers && allUsers.length > 0) {
                    const hostUser = allUsers.find((u: any) => u.camps && Array.isArray(u.camps) && u.camps.includes(campId));
                    hostUserId = hostUser?.userId || null;
                }
            }
            
            if (!hostUserId) {
                console.error('Host user not found for camp:', campId);
                return;
            }
            
            // Get coach userId if assigned
            const coachUserId = session.instructor?.userId || null;
            
            // Send notifications to host (and coach if assigned)
            await NotificationOps.notifyRegistration(
                hostUserId,
                coachUserId,
                childNames,
                parentName,
                sessionTitle,
                sessionId,
                campId,
                action === 'register' ? 'registered' : 'unregistered'
            );
            
        } catch (error) {
            console.error('Error sending notifications:', error);
        }
    };

    // Helper function to add session to favorites
    const addSessionToFavorites = async (sessionId: string) => {
        if (!user || !sessionId) return;
        
        try {
            // Update local state
            const newLikedSessions = new Set(likedSessions);
            newLikedSessions.add(sessionId);
            setLikedSessions(newLikedSessions);
            
            // Update user in Firebase and local storage
            const updatedUser = {
                ...user,
                likedSessions: Array.from(newLikedSessions)
            };
            await DataOps.saveCustomDataToFirebase('user', user.userId, updatedUser, true);
            await DataOps.saveDataToStorage('user', updatedUser);
            setUser(updatedUser);
        } catch (error) {
            console.error('Error adding session to favorites:', error);
        }
    };

    // Helper function to remove session from favorites (only if no children are registered)
    const removeSessionFromFavoritesIfNeeded = async (sessionId: string) => {
        if (!user || !sessionId || !session) return;
        
        try {
            // Check if any active registrations remain for this parent on this session
            const regs = await DataOps.fetchActiveRegistrations(sessionId);
            const stillRegistered = (regs || []).some((r: any) => r.userId === user.userId);
            
            // Only remove from favorites if no children are registered
            if (!stillRegistered) {
                // Update local state
                const newLikedSessions = new Set(likedSessions);
                newLikedSessions.delete(sessionId);
                setLikedSessions(newLikedSessions);
                
                // Update user in Firebase and local storage
                const updatedUser = {
                    ...user,
                    likedSessions: Array.from(newLikedSessions)
                };
                await DataOps.saveCustomDataToFirebase('user', user.userId, updatedUser, true);
                await DataOps.saveDataToStorage('user', updatedUser);
                setUser(updatedUser);
            }
        } catch (error) {
            console.error('Error removing session from favorites:', error);
        }
    };

    const handleChildrenRegistration = async (selectedChildIds: string[], action: 'register' | 'unregister') => {
        try {
            if (action === 'register') {
                await updateRegisteredChildren(selectedChildIds);
                await updateChildrenUpcomingSessions(selectedChildIds);
                
                // Automatically add session to favorites when registering
                if (sessionId) {
                    await addSessionToFavorites(sessionId);
                }
                
                // Get child names for notification
                const childNames = selectedChildIds
                    .map(childId => {
                        const child = user.children.find(c => c.childId === childId);
                        return child ? `${child.firstName} ${child.lastName}` : '';
                    })
                    .filter(name => name !== '');
                
                // Send notifications
                await sendNotificationsToHostAndCoach(childNames, 'register');
                
                const childCount = selectedChildIds.length;
                const childText = childCount === 1 ? 'child' : 'children';
                toast.success(`Successfully registered ${childCount} ${childText} for this session!`);
                try { await DataOps.saveDataToStorage(`sessionsDirty_${user.userId}`, true); } catch {}
                try { setTimeout(() => window.location.reload(), 1200); } catch {}
            } else {
                await updateUnregisteredChildren(selectedChildIds);
                await removeChildrenUpcomingSessions(selectedChildIds);
                
                // Automatically remove session from favorites if no children are registered
                if (sessionId) {
                    await removeSessionFromFavoritesIfNeeded(sessionId);
                }
                
                // Get child names for notification
                const childNames = selectedChildIds
                    .map(childId => {
                        const child = user.children.find(c => c.childId === childId);
                        return child ? `${child.firstName} ${child.lastName}` : '';
                    })
                    .filter(name => name !== '');
                
                // Send notifications
                await sendNotificationsToHostAndCoach(childNames, 'unregister');
                
                const childCount = selectedChildIds.length;
                const childText = childCount === 1 ? 'child' : 'children';
                toast.success(`Successfully unregistered ${childCount} ${childText} from this session.`);
                try { await DataOps.saveDataToStorage(`sessionsDirty_${user.userId}`, true); } catch {}
                try { setTimeout(() => window.location.reload(), 1200); } catch {}
            }
        } catch (error) {
            console.error(`Error ${action}ing children:`, error);
            toast.error(`Failed to ${action}. Please try again.`);
        }
    };

    const handleOptionPress = (option) => {
        setSelectedOption(option);
        updateValue('registrationStatus', option);
        setIsOpen(false);
    };

    const renderOptions = () => {
        const availableOptions = Object.values(RegistrationStatus).filter(
            (option) => option !== selectedOption);

        return availableOptions.map((option) => {
            const config = getRegistrationStatusConfig(option);
            return (
                <Pressable key={option} style={[styles.banner, {
                    backgroundColor: config.backgroundColor,
                }]} onPress={() => handleOptionPress(option)}>
                    <Text style={[styles.bannerText, {color: config.color}]}>{config.text}</Text>
                </Pressable>
            );
        });
    };



    return (
        <>
            <Pressable onPress={onBannerPress} style={{zIndex: 10}} ref={bannerRef}>
                {(() => {
                    const config = getRegistrationStatusConfig(effectiveRegistrationStatus);
                    return (
                        <View style={[styles.banner, {
                            backgroundColor: config.backgroundColor,
                        }]}>
                            {isEditing ? (
                                <Text style={[styles.bannerText, {color: config.color}]}>
                                    {isHost ? RegistrationStatus.COACH_VIEW : selectedOption}
                                </Text>
                            ) : (
                                <Text style={[styles.bannerText, {color: config.color}]}>
                                    {config.text}
                                </Text>
                            )}
                        </View>
                    );
                })()}
            </Pressable>
            
            {/* Coach/Camper View Modal */}
            {showCoachModal && <CoachViewModal
                visible={showCoachModal}
                onClose={() => {
                    setShowCoachModal(false);
                }}
                session={session}
                currentUser={user}
                isCoach={isHost}
                initialView={initialView}
                initialDate={initialDate}
            />}
            
            {/* Waiver Acknowledgment Modal */}
            <WaiverAcknowledgeModal
                visible={showWaiverModal}
                onClose={() => setShowWaiverModal(false)}
                onAcknowledge={() => {
                    setShowWaiverModal(false);
                    setShowChildSelectionModal(true);
                }}
                waiverTitle={session?.waiverTitle}
                waiverUrl={session?.waiverUrl}
                sessionTitle={session?.title}
                campName={session?.campName}
            />

            {/* Child Selection Modal */}
            {user?.children && (
                <ChildSelectionModal
                    visible={showChildSelectionModal}
                    onClose={() => setShowChildSelectionModal(false)}
                    onConfirm={handleChildrenRegistration}
                    children={user.children}
                    alreadyRegisteredChildIds={registeredChildIds}
                />
            )}
        </>
    );
};

const styles = StyleSheet.create({
    banner: {
        paddingHorizontal: 10,
        paddingVertical: 8,
        borderRadius: 40,
        marginRight: 10,
        alignItems: 'center',
        zIndex: 10,
        marginBottom: 8,
        minWidth: 150,
    },
    bannerText: {
        fontSize: 13,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        color: 'white',
        zIndex: 2,
    },
    dropdownList: {
        marginTop: 2,
        backgroundColor: 'white',
        borderRadius: 15,
        top: 40,
        right: -10,
        minWidth: 150,
        position: 'absolute',
        zIndex: 10,
    },
});

export default RegistrationBanner;
